<template>
    <div class="image-brief-container" v-loading="loading">
        <div class="page">
            <div class="page-left">
                <div class="title">上传图片</div>
                <div class="upload-area" @click="triggerFileSelect" @drop.prevent="handleDrop" @dragover.prevent>
                    <div v-if="!imagePreview">
                        <img src="../images/upload.png" alt="上传" class="upload-icon">
                        <div class="upload-text">点击或拖拽上传图片</div>
                        <div class="upload-brief">支持10MB图片，PNG、JPG、JPEG格式</div>
                    </div>
                    <div v-else class="preview-container">
                        <img :src="imagePreview" alt="预览" class="preview-image">
                        <div class="change-image">点击更换图片</div>
                    </div>
                    <input 
                        type="file" 
                        ref="fileInput" 
                        @change="handleFileSelect" 
                        accept="image/png,image/jpeg,image/jpg"
                        style="display: none;"
                    >
                </div>
                
                <div class="title mt24">简介描述</div>
                <div class="border2">
                    <el-input 
                        v-model="briefText" 
                        maxlength="200" 
                        style="width: 100%;height: 100%;"
                        placeholder="请描述您想要如何修改这张图片，例如：变成卡通风格、调整色调为暖色、添加雪景效果等" 
                        show-word-limit 
                        type="textarea" 
                    />
                </div>

                <div class="flex-col" @click="handleEditImage" :class="{ 'loading': loading }">
                    <div class="col-title" v-if="!loading">开始编辑</div>
                    <div class="col-title" v-else>编辑中...</div>
                    <div class="col-brief">（编辑消耗5积分）</div>
                </div>
            </div>
        </div>
        
        <div class="page-right">
            <div class="title">编辑记录</div>
            <div class="line"></div>
            <div class="active"></div>
            <div class="list" v-loading="historyLoading">
                <template v-if="editHistory.length > 0">
                    <template v-for="(record, index) in editHistory" :key="record.id">
                        <div class="item-wrapper">
                            <div class="item">
                                <img class="item-logo" src="../images/logo.png" alt="">
                                <div class="item-title">图片编辑</div>
                                <div class="item-time">{{ formatDate(record.created_at) }}</div>
                            </div>
                            <div class="content-area">
                                <div class="image-compare">
                                    <div class="image-box edited-images">
                                        <div class="image-label">编辑后 ({{ getEditedImageCount(record) }}张)</div>
                                        <div class="edited-image-grid">
                                            <img 
                                                v-for="(imgUrl, imgIndex) in getEditedImages(record)" 
                                                :key="imgIndex" 
                                                :src="imgUrl" 
                                                :alt="'编辑后' + (imgIndex + 1)" 
                                                class="content-image"
                                                @click="previewImage(imgUrl)"
                                            >
                                        </div>
                                    </div>
                                </div>
                                <div class="brief-content">
                                    <div class="brief-label">编辑描述：</div>
                                    <div class="brief-text">{{ getBriefText(record) }}</div>
                                </div>
                            </div>
                            <div class="row">
                                <div class="d-box" @click="downloadImage(record.edited_image_url || record.edited_image_url)">
                                    <img class="d-box-img" src="../images/d.png" alt="">
                                    <div class="d-box-title">下载图片</div>
                                </div>
                                <div class="d-box" @click="reuseEdit(record)">
                                    <img class="d-box-img" src="../images/edit.png" alt="">
                                    <div class="d-box-title">重新编辑</div>
                                </div>
                            </div>
                        </div>
                    </template>
                </template>
                <div v-else class="no-history">
                    <p>暂无编辑记录</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, inject, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { ElMessage } from 'element-plus';
import { uploadImage, generateImageBrief, getImageBriefHistory } from '@/api/design';

// 获取store和provide
const store = useStore();
const openLoginDialog = inject('openLoginDialog', null);

// 页面逻辑
const fileInput = ref(null);
const imagePreview = ref(null);
const imageFile = ref(null);
const briefText = ref('');
const uploadedImageUrl = ref(null);

// Loading 状态
const loading = ref(false);
const historyLoading = ref(false);

// 编辑记录
const editHistory = ref([]);

// 检查用户是否已登录
const checkLogin = async () => {
    if (!store.getters.isLoggedIn) {
        ElMessage({
            message: '暂未登录',
            type: 'warning',
            duration: 3000
        });

        if (openLoginDialog) {
            openLoginDialog('login');
        }
        return false;
    }
    return true;
}

// 触发文件选择
const triggerFileSelect = () => {
    fileInput.value.click();
}

// 处理文件选择
const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
        processFile(file);
    }
}

// 处理拖拽
const handleDrop = (event) => {
    const file = event.dataTransfer.files[0];
    if (file) {
        processFile(file);
    }
}

// 处理文件
const processFile = (file) => {
    // 验证文件类型
    if (!file.type.match('image/(png|jpeg|jpg)')) {
        ElMessage({
            message: '只支持PNG、JPG、JPEG格式的图片',
            type: 'error',
            duration: 3000
        });
        return;
    }

    // 验证文件大小
    if (file.size > 10 * 1024 * 1024) {
        ElMessage({
            message: '图片大小不能超过10MB',
            type: 'error',
            duration: 3000
        });
        return;
    }

    imageFile.value = file;
    
    // 创建预览
    const reader = new FileReader();
    reader.onload = (e) => {
        imagePreview.value = e.target.result;
    };
    reader.readAsDataURL(file);
}

// 获取编辑记录
const fetchEditHistory = async () => {
    if (!(await checkLogin())) return;
    
    historyLoading.value = true;
    try {
        const params = {
            limit: 20,
            offset: 0
        };
        const response = await getImageBriefHistory(params);
        editHistory.value = response.data.data || [];
    } catch (error) {
        console.error('获取编辑记录失败:', error);
        editHistory.value = [];
    } finally {
        historyLoading.value = false;
    }
}

// 组件挂载时获取编辑记录
onMounted(() => {
    fetchEditHistory();
})

// 监听用户登录状态变化
watch(
    () => store.getters.isLoggedIn,
    (newVal, oldVal) => {
        if (newVal && !oldVal) {
            fetchEditHistory();
        }
    }
);

// 格式化日期
const formatDate = (dateString) => {
    if (!dateString) return '未知时间';

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`;
}

// 获取原图URL
const getOriginalImageUrl = (record) => {
    return record.original_image_url || record.image_url;
}

// 获取编辑描述文本
const getBriefText = (record) => {
    return record.prompt || '无描述';
}

// 获取编辑后的图片列表
const getEditedImages = (record) => {
    if (record.all_edited_image_urls && Array.isArray(record.all_edited_image_urls)) {
        return record.all_edited_image_urls;
    }
    if (record.edited_image_url) {
        return [record.edited_image_url];
    }
    return [];
}

// 获取编辑后图片数量
const getEditedImageCount = (record) => {
    return getEditedImages(record).length;
}

// 预览图片
const previewImage = (imageUrl) => {
    window.open(imageUrl, '_blank');
}

// 编辑图片
const handleEditImage = async () => {
    if (!(await checkLogin())) return;

    if (!imageFile.value) {
        ElMessage({
            message: '请先上传图片',
            type: 'warning',
            duration: 3000
        });
        return;
    }

    if (!briefText.value || !briefText.value.trim()) {
        ElMessage({
            message: '请输入编辑描述',
            type: 'warning',
            duration: 3000
        });
        return;
    }

    // 检查积分是否足够
    const requiredPoints = 5;
    const currentPoints = store.getters.userInfo?.points || store.getters.userInfo?.credit || 0;

    if (currentPoints < requiredPoints) {
        ElMessage({
            message: '积分不足，请先充值',
            type: 'warning',
            duration: 3000
        });
        return;
    }

    loading.value = true;

    try {
        // 先上传图片
        ElMessage({
            message: '正在上传图片...',
            type: 'info',
            duration: 2000
        });

        const formData = new FormData();
        formData.append('image', imageFile.value);
        
        const uploadResponse = await uploadImage(formData);
        uploadedImageUrl.value = uploadResponse.data.data.url;

        // 编辑图片
        ElMessage({
            message: '正在编辑图片...',
            type: 'info',
            duration: 2000
        });

        const editResponse = await generateImageBrief({
            image_url: uploadedImageUrl.value,
            prompt: briefText.value
        });

        if (editResponse.data.code === 10000) {
            ElMessage({
                message: '图片编辑成功',
                type: 'success',
                duration: 3000
            });

            // 刷新编辑记录
            await fetchEditHistory();
            
            // 清空表单
            briefText.value = '';
        }
    } catch (error) {
        console.error('编辑图片失败:', error);
        ElMessage({
            message: error.response?.data?.error || '编辑图片失败，请稍后重试',
            type: 'error',
            duration: 3000
        });
    } finally {
        loading.value = false;
    }
}

// 重新编辑
const reuseEdit = (record) => {
    // 加载原有的编辑描述
    briefText.value = getBriefText(record);
    
    ElMessage({
        message: '已加载编辑描述，请上传图片后点击开始编辑',
        type: 'info',
        duration: 3000
    });
    
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
}

// 下载图片
const downloadImage = (url) => {
    if (!url) {
        ElMessage({
            message: '图片链接不存在',
            type: 'warning',
            duration: 2000
        });
        return;
    }
    
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'edited-image.png');
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
</script>

<style scoped>
.image-brief-container {
    display: flex;
    align-items: flex-start;
}

.page-left {
    width: 490px;
    min-height: 80vh;
    padding: 20px;
    box-sizing: border-box;
    background: #1D1D1D;
    border-radius: 16px;
}

.page-right {
    flex: 1;
    margin-left: 16px;
    min-height: 80vh;
}

.title {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 16px;
    color: #FFFFFF;
}

.mt24 {
    margin-top: 24px;
}

.upload-area {
    width: 450px;
    height: 300px;
    background: #2B2C2C;
    border-radius: 8px;
    border: 2px dashed #5EFF00;
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;
    overflow: hidden;
}

.upload-area:hover {
    background: #333;
    border-color: #00FBFF;
}

.upload-icon {
    width: 60px;
    height: 60px;
}

.upload-text {
    font-family: PingFang SC, PingFang SC;
    font-size: 16px;
    color: #FFFFFF;
    margin-top: 10px;
}

.upload-brief {
    font-family: PingFang SC, PingFang SC;
    font-size: 12px;
    color: #6A6B6E;
    margin-top: 5px;
}

.preview-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.preview-image {
    max-width: 100%;
    max-height: 260px;
    object-fit: contain;
}

.change-image {
    font-family: PingFang SC, PingFang SC;
    font-size: 14px;
    color: #5EFF00;
    margin-top: 10px;
}

.border2 {
    width: 450px;
    height: 120px;
    background: #2B2C2C;
    border-radius: 8px;
    border: 1px solid #2B2C2C;
    margin-top: 10px;
}

.flex-col {
    width: 450px;
    height: 60px;
    background: #5EFF00;
    border-radius: 8px;
    border: 1px solid #00FBFF;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
    cursor: pointer;
}

.flex-col.loading {
    background: #cccccc;
    border-color: #999999;
    cursor: not-allowed;
}

.col-title {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 16px;
    color: #222222;
}

.col-brief {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 14px;
    color: #328800;
}

.line {
    width: 100%;
    margin-top: 16px;
    height: 0px;
    border: 1px solid #2B2C2C;
}

.active {
    width: 64px;
    height: 0px;
    border: 3px solid #5EFF00;
    font-size: 16px;
    margin-top: -6px;
}

.list {
    height: 80vh;
    overflow-y: scroll;
    margin-top: 20px;
}

.no-history {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #6A6B6E;
    font-size: 16px;
    text-align: center;
}

.item-wrapper {
    margin-bottom: 40px;
    padding-bottom: 20px;
    border-bottom: 1px solid #2B2C2C;
}

.item {
    display: flex;
    align-items: center;
    margin-top: 20px;
}

.item-logo {
    width: 24px;
    height: 24px;
}

.item-title {
    margin-left: 12px;
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 14px;
    color: #FFFFFF;
}

.item-time {
    margin-left: 12px;
    font-family: Arial, Arial;
    font-weight: 400;
    font-size: 14px;
    color: #6A6B6E;
}

.content-area {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.image-compare {
    display: flex;
    align-items: center;
    justify-content: space-around;
    gap: 20px;
}

.image-box {
    flex: 1;
    text-align: center;
}

.image-box.edited-images {
    flex: 2;
}

.edited-image-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
}

.edited-image-grid .content-image {
    cursor: pointer;
    transition: transform 0.3s, border-color 0.3s;
}

.edited-image-grid .content-image:hover {
    transform: scale(1.05);
    border-color: #5EFF00;
}

.image-label {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 14px;
    color: #5EFF00;
    margin-bottom: 10px;
}

.arrow-icon {
    font-size: 32px;
    color: #5EFF00;
    font-weight: bold;
}

.content-image {
    width: 400px;
    height: 400px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid #2B2C2C;
}

.brief-content {
    flex: 1;
    background: #2B2C2C;
    border-radius: 8px;
    padding: 15px;
}

.brief-label {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 14px;
    color: #5EFF00;
    margin-bottom: 10px;
}

.brief-text {
    font-family: PingFang SC, PingFang SC;
    font-size: 14px;
    color: #FFFFFF;
    line-height: 1.6;
}

.row {
    display: flex;
    align-items: center;
    margin-top: 20px;
}

.d-box {
    width: 112px;
    height: 32px;
    background: #17181A;
    border-radius: 8px;
    border: 1px solid #2B2C2C;
    display: flex;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
}

.d-box:first-child {
    margin-left: 0px;
}

.d-box-img {
    width: 16px;
    height: 16px;
}

.d-box-title {
    font-family: Arial, Arial;
    font-weight: normal;
    font-size: 14px;
    color: #FFFFFF;
    margin-left: 8px;
}

/* 滚动条样式 */
::-webkit-scrollbar {
    width: 12px;
    height: 12px;
}

::-webkit-scrollbar-track {
    background: #090909;
    border-radius: 6px;
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #121212, #939292);
    border-radius: 6px;
}

::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #666, #444);
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
    .image-brief-container {
        flex-direction: column;
    }

    .page-left {
        width: 100%;
        min-height: auto;
        margin-bottom: 16px;
    }

    .page-right {
        width: 100%;
        margin-left: 0;
        min-height: auto;
    }

    .upload-area,
    .border2,
    .flex-col {
        width: 100%;
    }

    .content-area {
        flex-direction: column;
    }

    .content-image {
        width: 100%;
    }
}
</style>
